import cx from 'classnames';
import Image, { ImageProps } from 'next/image';
import { HTMLAttributeAnchorTarget, MouseEventHandler } from 'react';

import { LocalHeroBadge } from '../../../../shared/components/LocalHero/Badge/LocalHeroBadge';
import { RATIO, Ratio } from '../../ratio/Ratio';
import { ReviewRating, ReviewRatingProps } from '../../review/rating/ReviewRating';
import { Typography } from '../../typography/Typography';

import style from './AgencyCard.module.css';

type Props = {
    description: string;
    image: ImageProps;
    name: string;
    smallScreenDisplayHeader?: boolean;
    url: string;
    onClick?: MouseEventHandler<HTMLElement>;
    reviewRatingProps?: ReviewRatingProps;
    target?: HTMLAttributeAnchorTarget;
    isLocalHero?: boolean;
    destinationName?: string;
    reviewRatingVariant?: 'default' | 'single-star';
    showReview?: boolean;
    showDescription?: boolean;
    lightText?: boolean;
};

export function AgencyCard(props: Props) {
    const {
        description,
        image,
        name,
        smallScreenDisplayHeader,
        url,
        onClick,
        reviewRatingProps,
        target,
        isLocalHero,
        destinationName,
        reviewRatingVariant,
        showReview,
        showDescription,
        lightText,
    } = props;

    const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
        if (onClick) {
            onClick(event);
        }

        if (target === '_blank' || event.ctrlKey) {
            return window.open(url, '_blank');
        }

        window.location.assign(url);
    };

    return (
        <div
            className={cx(style.card, {
                [style.cardDisplayHeader]: smallScreenDisplayHeader,
                [style.lightText]: lightText,
            })}
        >
            <div className={style.imageWrapper}>
                <Ratio ratio={AgencyCard.PICTURE_RATIO} className={style.imageRatio}>
                    {isLocalHero ? <LocalHeroBadge className={style.localHeroBadge} /> : null}
                    <Image
                        {...image}
                        className={cx(image.className, 'imageCover')}
                        alt={image.alt}
                        fill
                    />
                </Ratio>
            </div>
            <div className={style.name}>
                {destinationName ? (
                    <Typography scale="secondary-s-bold" className={style.destinationName}>
                        {destinationName}
                    </Typography>
                ) : null}
                <div onClick={handleClick} className={cx(style.agencyName, 'font-s-extrabold')}>
                    {name}
                </div>
            </div>
            {showDescription ? (
                <div className={cx(style.description, 'font-xs-regular')}>{description}</div>
            ) : null}

            {showReview && reviewRatingProps !== undefined ? (
                <ReviewRating
                    {...reviewRatingProps}
                    align="center"
                    className={style.reviewsRating}
                    size="small"
                    variant={reviewRatingVariant}
                />
            ) : null}
        </div>
    );
}

AgencyCard.PICTURE_RATIO = RATIO['3/4'];
